import { useMemo } from 'react';
import {
  AppBar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  MenuItem,
  MenuList,
  Paper,
  ThemeProvider,
  Toolbar,
  Typography,
} from '@mui/material';
import type { PaletteMode } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { createAppTheme, type ThemeColors } from '@/theme';

interface CustomThemePreviewProps {
  mode: PaletteMode;
  /** Every colour must be a valid `#RRGGBB` value — MUI refuses to build a theme otherwise. */
  colors: ThemeColors;
}

/**
 * A piece of the app in the colours being edited: header, page, card, buttons and a menu, built
 * with the same `createAppTheme` the app wears, so what it shows is what users get.
 */
export function CustomThemePreview({ mode, colors }: CustomThemePreviewProps) {
  const { t } = useTranslation();
  const theme = useMemo(() => createAppTheme(mode, colors), [mode, colors]);

  return (
    <ThemeProvider theme={theme}>
      <Box
        data-testid={`custom-theme-preview-${mode}`}
        sx={{ bgcolor: 'background.default', border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}
      >
        <AppBar position="static">
          <Toolbar variant="dense">
            <Typography variant="subtitle2">{t('settings.customTheme.previewHeader')}</Typography>
          </Toolbar>
        </AppBar>
        <Box sx={{ p: 1.5, display: 'flex', gap: 1.5, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <Card sx={{ flex: '1 1 180px' }}>
            <CardContent>
              <Typography variant="body2">{t('settings.customTheme.previewCard')}</Typography>
              <Typography variant="body2" color="text.secondary">
                {t('settings.customTheme.previewSecondaryText')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <Button size="small" variant="contained">{t('settings.customTheme.previewButton')}</Button>
                <Button size="small" variant="outlined">{t('settings.customTheme.previewButton')}</Button>
                <Chip size="small" color="secondary" label={t('settings.customTheme.colors.secondary')} />
              </Box>
            </CardContent>
          </Card>
          <Paper elevation={8} sx={{ flex: '0 1 160px' }}>
            <MenuList dense>
              <MenuItem>{t('settings.customTheme.previewMenuItem')}</MenuItem>
              <MenuItem selected>{t('settings.customTheme.previewSelected')}</MenuItem>
            </MenuList>
          </Paper>
        </Box>
      </Box>
    </ThemeProvider>
  );
}
